import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export {};

const BASE_URL = "/api";

type StoreItem = {
  store_name: string;
  store_name_kana: string;
  store_postal: string;
  store_city: string;
  store_address: string;
  phone: string;
  fax: string;
  email: string;
};

type StoreListData = {
  merchant_id: string;
  store_list_file_name?: string;
  store_list_file_path?: string;
  store_name?: string;
  store_name_kana?: string;
  store_postal?: string;
  store_city?: string;
  store_address?: string;
  phone?: string;
  fax?: string;
  email?: string;
  store_items?: StoreItem[];
  is_completed: boolean;
  updated_at?: string;
};

type FieldDef = { key: keyof StoreItem; label: string };

const getStaffAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("staff_google_token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const normalizeStoreItem = (item?: Partial<StoreItem> | null): StoreItem => ({
  store_name: item?.store_name || "",
  store_name_kana: item?.store_name_kana || "",
  store_postal: item?.store_postal || "",
  store_city: item?.store_city || "",
  store_address: item?.store_address || "",
  phone: item?.phone || "",
  fax: item?.fax || "",
  email: item?.email || "",
});

function ReadonlyInputBox({
  label,
  value,
  required,
}: {
  label: string;
  value: string | undefined | null;
  required?: boolean;
}) {
  const displayValue = value && String(value).trim() ? String(value) : "—";

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>

      <div
        style={{
          width: "100%",
          height: 44,
          padding: "0 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#f8fafc",
          color: displayValue === "—" ? "#94a3b8" : "#0f172a",
          fontSize: 14,
          fontWeight: 700,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {displayValue}
      </div>
    </div>
  );
}

function EditRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "220px 1fr",
        gap: 12,
        padding: "10px 12px",
        borderBottom: "1px solid #e2e8f0",
        alignItems: "center",
      }}
    >
      <div style={{ fontWeight: 900, color: "#0f172a" }}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          outline: "none",
          fontWeight: 700,
          color: "#0f172a",
          background: "#fff",
        }}
      />
    </div>
  );
}

export default function StaffFormViewStoreList() {
  const navigate = useNavigate();
  const { merchantId } = useParams();

  const contentRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<StoreListData | null>(null);
  const [draft, setDraft] = useState<StoreListData | null>(null);

  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const fields: FieldDef[] = useMemo(
    () => [
      { key: "store_name", label: "店舗名（全角）" },
      { key: "store_name_kana", label: "店舗名フリガナ（半角）" },
      { key: "store_postal", label: "店舗所在地 郵便番号" },
      { key: "store_city", label: "店舗所在地 ～市町村丁目（全角）" },
      { key: "store_address", label: "店舗所在地 番地以降（全角）" },
      { key: "phone", label: "電話番号" },
      { key: "fax", label: "FAX番号" },
      { key: "email", label: "Email" },
    ],
    []
  );

  const readonlyStoreItems = useMemo(() => {
    if (!data) return [];

    const items = Array.isArray(data.store_items) ? data.store_items : [];
    if (items.length > 0) return items.map((item) => normalizeStoreItem(item));

    return [
      normalizeStoreItem({
        store_name: data.store_name,
        store_name_kana: data.store_name_kana,
        store_postal: data.store_postal,
        store_city: data.store_city,
        store_address: data.store_address,
        phone: data.phone,
        fax: data.fax,
        email: data.email,
      }),
    ];
  }, [data]);

  const fetchData = async () => {
    if (!merchantId) return;

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form6_store_list`, {
        headers: getStaffAuthHeaders(),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "fetch failed");

      const apiItems = Array.isArray(json?.data?.store_items) ? json.data.store_items : [];
      const normalizedItems =
        apiItems.length > 0
          ? apiItems.map((item: any) => normalizeStoreItem(item))
          : [
              normalizeStoreItem({
                store_name: json?.data?.store_name || "",
                store_name_kana: json?.data?.store_name_kana || "",
                store_postal: json?.data?.store_postal || "",
                store_city: json?.data?.store_city || "",
                store_address: json?.data?.store_address || "",
                phone: json?.data?.phone || "",
                fax: json?.data?.fax || "",
                email: json?.data?.email || "",
              }),
            ];

      const d: StoreListData = {
        merchant_id: merchantId,
        store_list_file_name: json?.data?.store_list_file_name || "",
        store_list_file_path: json?.data?.store_list_file_path || "",
        store_name: normalizedItems[0]?.store_name || "",
        store_name_kana: normalizedItems[0]?.store_name_kana || "",
        store_postal: normalizedItems[0]?.store_postal || "",
        store_city: normalizedItems[0]?.store_city || "",
        store_address: normalizedItems[0]?.store_address || "",
        phone: normalizedItems[0]?.phone || "",
        fax: normalizedItems[0]?.fax || "",
        email: normalizedItems[0]?.email || "",
        store_items: normalizedItems,
        is_completed: !!json?.data?.is_completed,
        updated_at: json?.data?.updated_at,
      };

      setData(d);
      setDraft(d);
      setLoading(false);
    } catch (e: any) {
      setError(e?.message || "フォーム⑥（店舗一覧）の取得に失敗しました。");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId]);

  const downloadPdf = async () => {
    if (!contentRef.current || !merchantId) return;

    try {
      setDownloading(true);

      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pdfWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        position -= pdfHeight;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`フォーム6_店舗一覧_${merchantId}.pdf`);
    } catch {
      alert("PDFの作成に失敗しました。もう一度お試しください。");
    } finally {
      setDownloading(false);
    }
  };

  const saveEdits = async () => {
    if (!merchantId || !draft) return;

    try {
      setSaving(true);
      setError("");

      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form6_store_list`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...getStaffAuthHeaders() },
        body: JSON.stringify({
          store_name: draft.store_name || "",
          store_name_kana: draft.store_name_kana || "",
          store_postal: draft.store_postal || "",
          store_city: draft.store_city || "",
          store_address: draft.store_address || "",
          phone: draft.phone || "",
          fax: draft.fax || "",
          email: draft.email || "",
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.detail || "save failed");

      setEditMode(false);
      await fetchData();
      alert("保存しました。");
    } catch (e: any) {
      setError(e?.message || "保存に失敗しました。");
    } finally {
      setSaving(false);
    }
  };

  const openStoreListFileInBrowser = async () => {
    if (!merchantId) return;

    try {
      setError("");

      const res = await fetch(`${BASE_URL}/staff/forms/${merchantId}/form6_store_list/view`, {
        method: "GET",
        headers: getStaffAuthHeaders(),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "ファイルを開けませんでした（認証エラー）");
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      setError(e?.message || "ファイルを開けませんでした。");
    }
  };

  const toggleEditMode = () => {
    if (!data) return;

    if (editMode) {
      setDraft({ ...data });
      setEditMode(false);
      setError("");
      return;
    }

    setDraft({ ...data });
    setEditMode(true);
    setError("");
  };

  return (
    <StaffLayout title="フォーム⑥ 店舗一覧（確認）" active="progress">
      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <button
          onClick={() => navigate(`/staff/progress/${merchantId}`)}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#fff",
            color: "#0f172a",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          進捗詳細へ戻る
        </button>

        <button
          onClick={downloadPdf}
          disabled={downloading || loading || !!error}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #0b1f3b",
            background: downloading ? "#1e293b" : "#0b1f3b",
            color: "#fff",
            fontWeight: 900,
            cursor: downloading ? "not-allowed" : "pointer",
            opacity: downloading || loading || !!error ? 0.8 : 1,
          }}
        >
          {downloading ? "PDF作成中..." : "PDFダウンロード"}
        </button>

        <button
          onClick={toggleEditMode}
          disabled={loading || !!error || !data}
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid #0b1f3b",
            background: editMode ? "#fff" : "#0b1f3b",
            color: editMode ? "#0b1f3b" : "#fff",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          {editMode ? "編集をやめる" : "編集する（スタッフ）"}
        </button>

        {editMode && (
          <button
            onClick={saveEdits}
            disabled={saving || !draft}
            style={{
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #16a34a",
              background: saving ? "#14532d" : "#16a34a",
              color: "#fff",
              fontWeight: 900,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "保存中..." : "保存"}
          </button>
        )}
      </div>

      {loading && <div style={{ color: "#475569", fontWeight: 800 }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginTop: 10,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <div ref={contentRef} style={{ display: "grid", gap: 12, background: "#ffffff" }}>
          <div
            style={{
              border: "1px solid #bae6fd",
              background: "#f0f9ff",
              borderRadius: 14,
              padding: 14,
            }}
          >
            <div style={{ fontWeight: 900, marginBottom: 6 }}>フォーム状態</div>
            <div style={{ fontWeight: 900, color: data.is_completed ? "#166534" : "#9a3412" }}>
              {data.is_completed ? "完了（保存済み）" : "未完了（または未保存）"}
            </div>
            <div style={{ marginTop: 6, color: "#475569", fontWeight: 800, fontSize: 13 }}>
              加盟店ID：{merchantId}
              {data.updated_at ? `　｜　最終更新：${data.updated_at}` : ""}
            </div>
          </div>

          <div
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 14,
              background: "#fff",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: 12,
                fontWeight: 900,
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              入力内容（{editMode ? "編集モード（スタッフ）" : "読み取り専用"}）
            </div>

            {!editMode && (
              <div style={{ padding: 12 }}>
                <div
                  style={{
                    border: "1px solid #cbd5e1",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "#fff",
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      padding: "10px 12px",
                      fontWeight: 900,
                      background: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    店舗一覧別紙添付（PDF / Excel）
                  </div>

                  <div style={{ padding: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6 }}>
                      店舗一覧ファイル名
                    </div>

                    {data.store_list_file_name && data.store_list_file_name.trim() ? (
                      <button
                        type="button"
                        onClick={openStoreListFileInBrowser}
                        style={{
                          border: "1px solid #bfdbfe",
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          padding: "10px 12px",
                          borderRadius: 10,
                          fontWeight: 900,
                          cursor: "pointer",
                        }}
                      >
                        {data.store_list_file_name}
                      </button>
                    ) : (
                      <div style={{ color: "#94a3b8", fontWeight: 900 }}>—</div>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    border: "1px solid #cbd5e1",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "#fff",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 12px",
                      fontWeight: 900,
                      background: "#f8fafc",
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    店舗情報
                  </div>

                  <div style={{ padding: 12 }}>
                    {readonlyStoreItems.map((item, index) => (
                      <div
                        key={index}
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 12,
                          padding: 12,
                          marginBottom: 14,
                          background: index === 0 ? "#ffffff" : "#f8fafc",
                        }}
                      >
                        <div style={{ fontWeight: 900, color: "#0f172a", marginBottom: 12 }}>
                          店舗情報 {index + 1}
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                            gap: 20,
                            alignItems: "start",
                          }}
                        >
                          {fields.map((f) => (
                            <ReadonlyInputBox
                              key={`${index}-${String(f.key)}`}
                              label={f.label}
                              value={item[f.key]}
                              required={f.key !== "fax"}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {editMode && draft && (
              <>
                <EditRow label="店舗名" value={draft.store_name || ""} onChange={(v) => setDraft({ ...draft, store_name: v })} />
                <EditRow label="店舗名（カナ）" value={draft.store_name_kana || ""} onChange={(v) => setDraft({ ...draft, store_name_kana: v })} />
                <EditRow label="郵便番号" value={draft.store_postal || ""} onChange={(v) => setDraft({ ...draft, store_postal: v })} />
                <EditRow label="住所（市区町村）" value={draft.store_city || ""} onChange={(v) => setDraft({ ...draft, store_city: v })} />
                <EditRow label="住所（番地以降）" value={draft.store_address || ""} onChange={(v) => setDraft({ ...draft, store_address: v })} />
                <EditRow label="電話番号" value={draft.phone || ""} onChange={(v) => setDraft({ ...draft, phone: v })} />
                <EditRow label="FAX番号" value={draft.fax || ""} onChange={(v) => setDraft({ ...draft, fax: v })} />
                <EditRow label="メールアドレス" value={draft.email || ""} onChange={(v) => setDraft({ ...draft, email: v })} />
              </>
            )}
          </div>
        </div>
      )}
    </StaffLayout>
  );
}
